import { Injectable } from "@angular/core";
import { RegionEnum } from "@edp-enums/region.enum";
import { ESRequestsOperationsFiltersFactory, IEsRequestsOperationsFilter } from "@edp-models/ES/es-request-operatios.model";
import { ServiceES } from "@edp-models/ES/es-service.model";
import { RequestsOperationsESService } from "@edp-services/ES/request-operations-es.services";
import { ServiceESService } from "@edp-services/ES/service-es.service";
import { RegionService } from "@edp-services/region.service";
import {
    ERequestsAndOperationsESActions,
    GetRequestsAndOperationsES,
    GetServicesES,
    SelectFiltersES,
    SetFiltersES,
    SetLoadedRequestsAndOperationsES,
    SetLoadedServicesES,
    SetLocalSupportListES,
    SetNewRequestES,
    SetRequestsAndOperationsES,
    SetServicesES,
} from "@edp-store/actions/ES/request-operations-es.actions";
import { ELayoutActions, ShowLoader } from "@edp-store/actions/layout.actions";
import { RequestsOperationsGetServicesES } from "@edp-store/selectors/ES/request-operations-es.selectors";
import { getRouterStateES } from "@edp-store/selectors/ES/router-es.selector";
import { IAppEsState } from "@edp-store/state/ES/app-es.state";
import { IAppState } from "@edp-store/state/app.state";
import { Actions, Effect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, filter, mergeMap, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { of } from "rxjs";
import { ESRequestsPetitionsFactory, EsRequestPetitions } from "@edp-models/ES/es-petitions.model";

@Injectable()
export class RequestsOperationsESEffects {
    @Effect()
    loadAvailableEs$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsESActions.GetRequestsAndOperations),

        filter((action: GetRequestsAndOperationsES) => {
            return !!action.payload.contract;
        }),
        switchMap((action: GetRequestsAndOperationsES) => {
            if (!this.isRegionES()) {
                return of(new ShowLoader(false));
            }
            if (action.payload.localSupportList) {
                this.store.dispatch(new ShowLoader(true));
            }
            return this.requestsOperationsService.getList();
        }),
        mergeMap((requestsAndOperations: Array<any>) => {
            if (!this.isRegionES() || !requestsAndOperations?.length) {
                return of(new ShowLoader(false));
            }
            const request: EsRequestPetitions[] = requestsAndOperations.map(data => ESRequestsPetitionsFactory.create(data));
            return of(
                new SetRequestsAndOperationsES(request),
                new SelectFiltersES(),
                new SetLoadedRequestsAndOperationsES(true),
                new SetLocalSupportListES(false),
                new ShowLoader(false)
            );
        })
    );

    @Effect()
    setNewRequestEs$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsESActions.EsSetNewRequest),
        switchMap((action: SetNewRequestES) => {
            if (!this.isRegionES()) {
                return of();
            }
            this.store.dispatch(new ShowLoader(true));
            return this.requestsOperationsService.setNewRequestES(action.payload.request).pipe(
                switchMap(response => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    { type: ERequestsAndOperationsESActions.SetLocalSupportList, payload: true },
                    {
                        type: ERequestsAndOperationsESActions.GetRequestsAndOperations,
                        payload: {
                            contract: action.payload.contract,
                            localSupportList: true,
                        },
                    },
                    {
                        type: ERequestsAndOperationsESActions.SetNewRequestStatus,
                        payload: {
                            success: true,
                            externalCategory: action.payload.request.externalCategory,
                        },
                    },
                ]),
                catchError(error => [
                    { type: ELayoutActions.ShowLoader, payload: false },
                    {
                        type: ERequestsAndOperationsESActions.SetNewRequestStatus,
                        payload: {
                            success: false,
                            externalCategory: action.payload.request.externalCategory,
                        },
                    },
                ])
            );
        })
    );

    @Effect()
    selectFiltersES$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsESActions.EsSelectFilters),
        withLatestFrom(this.storeEs.select(RequestsOperationsGetServicesES), this.storeEs.select(getRouterStateES)),
        switchMap(([, serviceData, router]: [SelectFiltersES, ServiceES[], any]) => {
            if (!this.isRegionES()) {
                return of();
            }
            const filtersList = ESRequestsOperationsFiltersFactory.generateRequestsAndOperationsFilters(serviceData);
            const queryParams = router.state.queryParams;
            if (Object.keys(queryParams).length > 0) {
                Object.keys(queryParams).forEach(param => {
                    if (["service", "status"].includes(param)) {
                        const queryFilter = filtersList.find(filter => filter.urlName === param);
                        queryFilter.hasOptionSelected = true;
                        queryFilter.allSelected = false;
                        this.selectFilterOption(queryParams[param], queryFilter);
                    }
                });
            }
            return of(new SetFiltersES(filtersList), new SetServicesES(serviceData));
        })
    );

    @Effect()
    loadServicesES$ = this.actions$.pipe(
        ofType(ERequestsAndOperationsESActions.EsGetServices),
        tap(() => this.store.dispatch(new ShowLoader(true))),
        switchMap((action: GetServicesES) => {
            return this.serviceService
                .getGrouped({ segment: "B2C", contractId: action.payload })
                .pipe(catchError((error: Error) => of(error)));
        }),
        switchMap((serviceData: ServiceES[] | Error) => {
            if (serviceData instanceof Error) {
                return of(new ShowLoader(false));
            }
            return of(new SetServicesES(serviceData), new SetLoadedServicesES(true), new ShowLoader(false));
        })
    );

    private selectFilterOption(params: string | [string], filter: IEsRequestsOperationsFilter) {
        if (Array.isArray(params)) {
            params.forEach(option => {
                try {
                    filter.options.find(opt => opt.name === option).selected = true;
                } catch (error) {}
            });
        } else {
            try {
                filter.options.find(opt => opt.name === params).selected = true;
            } catch (error) {}
        }
    }

    private isRegionES(): boolean {
        return this.regionService.getRegion() === RegionEnum.ES;
    }

    constructor(
        private requestsOperationsService: RequestsOperationsESService,
        private actions$: Actions,
        private store: Store<IAppState>,
        private storeEs: Store<IAppEsState>,
        private serviceService: ServiceESService,
        private regionService: RegionService
    ) {}
}
